/**
 * Productdetail (US-006, US-007, US-022, US-027, US-028, US-029, design-handoff scherm 5).
 *
 * Toont per prijsbron de meting van vandaag met status, verschil en percentage ten opzichte van
 * onze eigen prijs, een lijndiagram over dertig dagen, en de dagrecords van de laatste tien
 * dagen. Beheerders kunnen een mislukte bron opnieuw laten controleren en het bewijs bekijken.
 */

import {
  formatDayLabel,
  formatEuro,
  MEASUREMENT_STATUS_LABELS,
  sorteerOpPrijs,
  type MeasurementStatus,
} from '@prijsmonitor/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ApiError, api } from '../api/client.js';
import { BronLabel } from '../components/BronLabel.js';
import { VersusEigen } from '../components/VersusEigen.js';
import { Button, Card, Chip, Notice, ui } from '../components/ui/index.js';
import { useSession } from '../session/SessionContext.js';
import { PriceChart } from './PriceChart.js';
import styles from './ProductDetailScreen.module.css';

interface SourceRow {
  id: string;
  name: string;
  isOwn: boolean;
  url: string | null;
  domain: string | null;
  competitorSku: string | null;
  status: string;
  today: {
    priceCents: number | null;
    previousValidCents: number | null;
    changeCents: number | null;
    status: MeasurementStatus;
    statusDetail: string | null;
    percentVsOwn: number | null;
    evidenceId: string | null;
  } | null;
  series: Array<{ dateKey: string; priceCents: number | null }>;
}

interface Detail {
  product: {
    id: string;
    name: string;
    ownSku: string;
    lengthLabel: string | null;
    color: string | null;
    colorCritical: boolean;
    foil: string | null;
    foilCritical: boolean;
    ownShopUrl: string | null;
  };
  sources: SourceRow[];
  recentDays: {
    columns: Array<{
      sourceId: string;
      name: string;
      isOwn: boolean;
      url?: string | null;
      domain?: string | null;
    }>;
    days: Array<{
      dateKey: string;
      cells: Array<{
        sourceId: string;
        priceCents: number | null;
        isChange: boolean;
        changeDirection: 'NONE' | 'UP' | 'DOWN';
        isLowest: boolean;
      }>;
    }>;
  };
}

const STATUS_TONE = (status: MeasurementStatus): 'success' | 'warn' =>
  status === 'SUCCESS' ? 'success' : 'warn';

export function ProductDetailScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useSession();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busySource, setBusySource] = useState<string | null>(null);
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [opPrijs, setOpPrijs] = useState(true);

  /*
   * De bronnen op de dagprijs, goedkoopste vooraan, met onze eigen regel bovenaan.
   *
   * De vastgelegde volgorde uit de bronconfigurator zei niets over de prijzen, dus stond er een rij
   * bedragen door elkaar en moest je ze allemaal lezen om te zien wie er onder ons zat. Nu is de
   * eerste concurrent onder de eigen regel de goedkoopste, en is dat één blik.
   *
   * Wel te schakelen: de vastgelegde volgorde is bruikbaar zodra je met de configuratie zelf bezig
   * bent, want dan wil je de bronnen zien staan zoals ze daar staan.
   */
  const bronnen = useMemo(() => {
    if (!detail) return [];
    if (!opPrijs) return detail.sources;
    return sorteerOpPrijs(
      detail.sources,
      (source) => source.isOwn,
      (source) => source.today?.priceCents ?? null,
    );
  }, [detail, opPrijs]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setDetail(await api.get<Detail>(`/api/products/${id}/detail`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Het product kon niet worden geladen.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function hercontroleer(sourceId: string): Promise<void> {
    setBusySource(sourceId);
    setError(null);
    try {
      await api.post(`/api/sources/${sourceId}/hercontroleren`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'De hercontrole is niet gelukt.');
    } finally {
      setBusySource(null);
    }
  }

  if (error && !detail) {
    return (
      <div className={ui.page}>
        <Notice tone="error">{error}</Notice>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={ui.page}>
        <p className={ui.hint}>Bezig met laden…</p>
      </div>
    );
  }

  const { product } = detail;

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}

      <div className={styles.header}>
        <Button onClick={() => void navigate('/prijsoverzicht')}>← Terug</Button>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{product.name}</h2>
          <div className={styles.subtitle}>
            Artikelnummer {product.ownSku}
            {product.ownShopUrl && (
              <>
                {' · '}
                <a href={product.ownShopUrl} target="_blank" rel="noreferrer noopener">
                  eigen productpagina
                </a>
              </>
            )}
          </div>
          <div className={styles.chips}>
            <Chip tone="info">Lengte {product.lengthLabel ?? '—'} · kritisch</Chip>
            {product.color && (
              <Chip tone={product.colorCritical ? 'info' : 'muted'}>
                Kleur {product.color} · {product.colorCritical ? 'kritisch' : 'niet kritisch'}
              </Chip>
            )}
            {product.foil && (
              <Chip tone={product.foilCritical ? 'info' : 'muted'}>
                Folie {product.foil} · {product.foilCritical ? 'kritisch' : 'niet kritisch'}
              </Chip>
            )}
          </div>
        </div>
        {isAdmin && (
          <Button
            variant="primary"
            onClick={() => void navigate(`/bronconfigurator?product=${product.id}`)}
          >
            + Concurrent toevoegen
          </Button>
        )}
      </div>

      <Card
        title="Prijsbronnen · vandaag"
        actions={
          <Button onClick={() => setOpPrijs(!opPrijs)}>
            {opPrijs ? 'Volgorde uit de configuratie' : 'Sorteer op prijs'}
          </Button>
        }
      >
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead>
              <tr>
                {/*
                  Het artikelnummer staat onder de bronnaam en niet in een eigen kolom, zoals in het
                  ontwerp. Het is een gegeven dat bij de bron hoort en dat je zelden leest; een eigen
                  kolom maakte de tabel breder en gaf het meer gewicht dan het verdient.
                */}
                <th>Bron</th>
                <th>Status</th>
                <th className={ui.numeric}>Dagprijs</th>
                <th className={ui.numeric}>Vorige</th>
                <th className={ui.numeric}>Δ prijs</th>
                <th className={ui.numeric}>% t.o.v. eigen</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {bronnen.map((source) => {
                const today = source.today;
                const mislukt = today !== null && today.status !== 'SUCCESS';
                return (
                  <tr key={source.id} className={source.isOwn ? styles.ownRow : undefined}>
                    <td>
                      <BronLabel
                        naam={source.name}
                        isOwn={source.isOwn}
                        url={source.url}
                        sku={source.competitorSku}
                      />
                    </td>
                    <td>
                      {today ? (
                        <Chip tone={STATUS_TONE(today.status)}>
                          {MEASUREMENT_STATUS_LABELS[today.status]}
                        </Chip>
                      ) : (
                        <Chip tone="muted">Nog niet gemeten</Chip>
                      )}
                      {today?.statusDetail && (
                        <div className={styles.statusDetail}>{today.statusDetail}</div>
                      )}
                    </td>
                    <td className={ui.numeric}>
                      {/* Dikgedrukt: van alle kolommen is dit het getal waar je naar kijkt. */}
                      {today?.priceCents === null || !today ? (
                        '—'
                      ) : (
                        <strong>{formatEuro(today.priceCents)}</strong>
                      )}
                    </td>
                    <td className={ui.numeric}>
                      {today?.previousValidCents ? formatEuro(today.previousValidCents) : '—'}
                    </td>
                    <td className={ui.numeric}>
                      {/*
                        Er stond een streepje, en dat betekende twee heel verschillende dingen: "de
                        prijs is gelijk gebleven" en "er is niets gemeten". Nu staat er alleen een
                        streepje wanneer er geen prijs is; is er wél een prijs en is die niet
                        veranderd, dan staat dat er.
                      */}
                      {today?.changeCents ? (
                        <span className={today.changeCents > 0 ? styles.up : styles.down}>
                          {today.changeCents > 0 ? '▲' : '▼'}{' '}
                          {formatEuro(Math.abs(today.changeCents))}
                        </span>
                      ) : today?.priceCents != null ? (
                        <span className={styles.ongewijzigd}>ongewijzigd</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={ui.numeric}>
                      {/* Onze eigen prijs is waar de rest tegen wordt afgezet; die vergelijkt niet. */}
                      {source.isOwn ? (
                        <span className={styles.ongewijzigd}>referentie</span>
                      ) : (
                        <VersusEigen procent={today?.percentVsOwn ?? null} />
                      )}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {isAdmin && mislukt && (
                          <Button
                            onClick={() => void hercontroleer(source.id)}
                            busy={busySource === source.id}
                          >
                            Opnieuw controleren
                          </Button>
                        )}
                        {today?.evidenceId && (
                          <Button onClick={() => setEvidenceId(today.evidenceId)}>Bewijs</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Prijsverloop · 30 dagen"
        description="Klik op een bron in de legenda om die te verbergen. Een onderbreking in de lijn betekent een lege meting."
      >
        <PriceChart
          series={detail.sources.map((source) => ({
            sourceId: source.id,
            name: source.name,
            isOwn: source.isOwn,
            points: source.series,
          }))}
          hidden={hidden}
          onToggle={(sourceId) =>
            setHidden((huidig) => {
              const next = new Set(huidig);
              if (next.has(sourceId)) next.delete(sourceId);
              else next.add(sourceId);
              return next;
            })
          }
        />
      </Card>

      <Card
        title="Dagrecords · laatste 10 dagen"
        description="Geel = laagste prijs die dag · ▲▼ = wijziging ten opzichte van de vorige dag"
        actions={
          <Button onClick={() => void navigate(`/prijshistorie?product=${product.id}`)}>
            Volledige historie →
          </Button>
        }
      >
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Dag</th>
                {detail.recentDays.columns.map((column) => (
                  <th key={column.sourceId}>
                    <BronLabel
                      naam={column.isOwn ? 'Eigen webshop' : column.name}
                      isOwn={column.isOwn}
                      url={column.url}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.recentDays.days.map((day) => (
                <tr key={day.dateKey}>
                  <td>
                    <strong>{formatDayLabel(day.dateKey)}</strong>
                  </td>
                  {day.cells.map((cell) => (
                    <td
                      key={cell.sourceId}
                      className={[
                        ui.numeric,
                        cell.isLowest ? styles.lowest : '',
                        cell.priceCents === null ? styles.emptyCell : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {cell.priceCents === null ? (
                        '— leeg'
                      ) : (
                        <>
                          {cell.isChange && (
                            <span
                              className={cell.changeDirection === 'UP' ? styles.up : styles.down}
                            >
                              {cell.changeDirection === 'UP' ? '▲' : '▼'}{' '}
                            </span>
                          )}
                          {formatEuro(cell.priceCents)}
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {evidenceId && (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Bewijsmateriaal"
          onClick={() => setEvidenceId(null)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <strong>Vastgelegd bewijs</strong>
              <Button onClick={() => setEvidenceId(null)}>Sluiten</Button>
            </div>
            <img
              className={styles.modalImage}
              src={`/api/evidence/${evidenceId}`}
              alt="Schermafdruk van de productpagina op het moment van de meting"
            />
          </div>
        </div>
      )}
    </div>
  );
}
